import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { 
  BarChart, Bar, LineChart, Line, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer,
  AreaChart, Area
} from 'recharts';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../../common/SafeIcon';
import { PageHeader, Card, Skeleton, useAdmChartColors } from './ui/AdminUI';
import { vehicleService } from '../../services/vehicleService';
import { leadsAPI } from '../../lib/supabase';

const { 
  FiTrendingUp, FiUsers, FiDollarSign, FiBarChart2, 
  FiActivity, FiCalendar, FiShoppingCart, FiPercent 
} = FiIcons;

const AdminAnalytics = () => {
  const chart = useAdmChartColors();
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState('month');
  const [analyticsData, setAnalyticsData] = useState({
    salesTrends: [],
    inventoryMetrics: [],
    leadSources: [],
    conversionFunnel: [],
    monthlyRevenue: [],
    topVehicles: []
  });

  const [kpis, setKpis] = useState({
    totalRevenue: 0,
    totalSales: 0,
    averageDealSize: 0,
    conversionRate: 0,
    inventoryTurnover: 0,
    leadToSaleTime: 0
  });

  useEffect(() => {
    loadAnalyticsData();
  }, [dateRange]);

  const loadAnalyticsData = async () => {
    setLoading(true);
    try {
      // Load vehicles and leads data
      const [vehicles, leads] = await Promise.all([
        vehicleService.getVehicles(),
        leadsAPI.getLeads()
      ]);

      // Generate sample analytics data
      const salesTrends = generateSalesTrends();
      const inventoryMetrics = generateInventoryMetrics(vehicles);
      const leadSources = generateLeadSources();
      const conversionFunnel = generateConversionFunnel();
      const monthlyRevenue = generateMonthlyRevenue();
      const topVehicles = generateTopVehicles(vehicles);

      setAnalyticsData({
        salesTrends,
        inventoryMetrics,
        leadSources,
        conversionFunnel,
        monthlyRevenue,
        topVehicles
      });

      // Calculate KPIs
      calculateKPIs(vehicles, leads);
    } catch (error) {
      console.error('Error loading analytics:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSalesTrends = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    return months.map(month => ({
      month,
      sales: Math.floor(Math.random() * 30) + 10,
      revenue: Math.floor(Math.random() * 500000) + 200000,
      units: Math.floor(Math.random() * 25) + 5
    }));
  };

  const generateInventoryMetrics = (vehicles) => {
    const types = ['New', 'Used', 'professional'];
    return types.map(type => ({
      type,
      count: Math.floor(Math.random() * 50) + 10,
      value: Math.floor(Math.random() * 1000000) + 500000,
      avgAge: Math.floor(Math.random() * 60) + 30
    }));
  };

  const generateLeadSources = () => {
    return [
      { name: 'Website', value: 35 },
      { name: 'Walk-in', value: 25 },
      { name: 'Phone', value: 20 },
      { name: 'Referral', value: 15 },
      { name: 'Social Media', value: 5 }
    ];
  };

  const generateConversionFunnel = () => {
    return [
      { stage: 'Leads', count: 500, conversion: 100 },
      { stage: 'Qualified', count: 350, conversion: 70 },
      { stage: 'Test Drive', count: 200, conversion: 40 },
      { stage: 'Negotiation', count: 120, conversion: 24 },
      { stage: 'Closed', count: 60, conversion: 12 }
    ];
  };

  const generateMonthlyRevenue = () => {
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun'];
    return months.map(month => ({
      month,
      new: Math.floor(Math.random() * 300000) + 100000,
      used: Math.floor(Math.random() * 200000) + 50000,
      service: Math.floor(Math.random() * 50000) + 10000,
      parts: Math.floor(Math.random() * 30000) + 5000
    }));
  };

  const generateTopVehicles = (vehicles) => {
    return vehicles.slice(0, 5).map(vehicle => ({
      name: `${vehicle.year} ${vehicle.make} ${vehicle.model}`,
      views: Math.floor(Math.random() * 500) + 100,
      inquiries: Math.floor(Math.random() * 50) + 10,
      testDrives: Math.floor(Math.random() * 20) + 5
    }));
  };

  const calculateKPIs = (vehicles, leads) => {
    const totalRevenue = Math.floor(Math.random() * 2000000) + 500000;
    const totalSales = Math.floor(Math.random() * 50) + 20;
    const averageDealSize = Math.floor(totalRevenue / totalSales);
    const conversionRate = ((Math.random() * 20) + 5).toFixed(1);
    const inventoryTurnover = ((Math.random() * 5) + 2).toFixed(1);
    const leadToSaleTime = Math.floor(Math.random() * 10) + 3;

    setKpis({
      totalRevenue,
      totalSales,
      averageDealSize,
      conversionRate,
      inventoryTurnover,
      leadToSaleTime
    });
  };

  const KPICard = ({ icon: Icon, title, value, subtitle, trend }) => (
    <div className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4">
      <div className="flex items-start justify-between gap-3">
        <p className="text-xs font-medium text-adm-text-mid">{title}</p>
        <SafeIcon icon={Icon} className="w-4 h-4 text-adm-text-lo shrink-0" />
      </div>
      <p className="mt-2 text-[26px] leading-none font-semibold text-adm-text-hi tabular-nums">{value}</p>
      <div className="mt-2 flex items-center gap-2">
        {trend ? (
          <span
            className={`inline-flex items-center gap-1 text-xs font-medium tabular-nums ${
              trend > 0 ? 'text-adm-success' : 'text-adm-danger'
            }`}
          >
            <span aria-hidden="true">{trend > 0 ? '\u25B2' : '\u25BC'}</span>
            {Math.abs(trend)}%
          </span>
        ) : null}
        {subtitle && <p className="text-xs text-adm-text-lo">{subtitle}</p>}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="space-y-5">
        <Skeleton className="h-7 w-52" />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
          {Array.from({ length: 6 }).map((_, i) => (
            <Skeleton key={i} className="h-[104px]" />
          ))}
        </div>
        <Skeleton className="h-[364px]" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <PageHeader
        title="Analytics"
        description="Sales, lead and inventory performance."
        actions={
          <>
            <label htmlFor="analytics-range" className="sr-only">Date range</label>
            <select
              id="analytics-range"
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
            >
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="quarter">Last Quarter</option>
              <option value="year">Last Year</option>
            </select>
          </>
        }
      />

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4">
        <KPICard
          icon={FiDollarSign}
          title="Total Revenue"
          value={`$${kpis.totalRevenue.toLocaleString()}`}
          trend={12.5}
        />
        <KPICard
          icon={FiShoppingCart}
          title="Total Sales"
          value={kpis.totalSales}
          subtitle="Units sold"
          trend={8.3}
        />
        <KPICard
          icon={FiTrendingUp}
          title="Avg Deal Size"
          value={`$${kpis.averageDealSize.toLocaleString()}`}
          trend={-2.1}
        />
        <KPICard
          icon={FiPercent}
          title="Conversion Rate"
          value={`${kpis.conversionRate}%`}
          subtitle="Lead to sale"
          trend={5.7}
        />
        <KPICard
          icon={FiActivity}
          title="Inventory Turnover"
          value={`${kpis.inventoryTurnover}x`}
          subtitle="Per month"
        />
        <KPICard
          icon={FiCalendar}
          title="Lead Time"
          value={`${kpis.leadToSaleTime} days`}
          subtitle="Avg to close"
          trend={-15.2}
        />
      </div>

      {/* Sales Trends Chart */}
      <div className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4">
        <h2 className="text-[15px] font-semibold text-adm-text-hi mb-4">Sales Trends</h2>
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={analyticsData.salesTrends}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chart.grid} />
            <XAxis dataKey="month" tickLine={false} axisLine={{ stroke: chart.grid }} />
            <YAxis yAxisId="left" tickLine={false} axisLine={false} width={44} />
            <YAxis yAxisId="right" orientation="right" tickLine={false} axisLine={false} width={56} />
            <Tooltip cursor={{ stroke: chart.grid, strokeDasharray: '3 3' }} />
            <Legend />
            <Line yAxisId="left" type="monotone" dataKey="sales" stroke={chart.blue} strokeWidth={1.75} dot={false} activeDot={{ r: 3 }} name="Units Sold" />
            <Line yAxisId="right" type="monotone" dataKey="revenue" stroke={chart.cmp} strokeWidth={1.75} strokeDasharray="4 3" dot={false} activeDot={{ r: 3 }} name="Revenue ($)" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Lead Sources Pie Chart */}
        <div className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4">
          <h2 className="text-[15px] font-semibold text-adm-text-hi mb-4">Lead Sources</h2>
          <ResponsiveContainer width="100%" height={300}>
            <PieChart>
              <Pie
                data={analyticsData.leadSources}
                cx="50%"
                cy="50%"
                labelLine={false}
                label={({ name, value }) => `${name}: ${value}%`}
                style={{ fontSize: 11 }}
                outerRadius={80}
                fill={chart.blue}
                dataKey="value"
              >
                {analyticsData.leadSources.map((entry, index) => (
                  <Cell
                    key={`cell-${index}`}
                    fill={[chart.blue, chart.cmp, chart.chart2, chart.chart4, chart.text][index % 5]}
                    stroke={chart.surface}
                  />
                ))}
              </Pie>
              <Tooltip cursor={{ stroke: chart.grid, strokeDasharray: '3 3' }} />
            </PieChart>
          </ResponsiveContainer>
        </div>

        {/* Conversion Funnel */}
        <div className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4">
          <h2 className="text-[15px] font-semibold text-adm-text-hi mb-4">Conversion Funnel</h2>
          <ResponsiveContainer width="100%" height={300}>
            <BarChart data={analyticsData.conversionFunnel}>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chart.grid} />
              <XAxis dataKey="stage" tickLine={false} axisLine={{ stroke: chart.grid }} />
              <YAxis tickLine={false} axisLine={false} width={44} />
              <Tooltip cursor={{ stroke: chart.grid, strokeDasharray: '3 3' }} />
              <Legend />
              <Bar dataKey="count" fill={chart.blue} radius={[3, 3, 0, 0]} name="Count" />
              <Bar dataKey="conversion" fill={chart.cmp} radius={[3, 3, 0, 0]} name="Conversion %" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Monthly Revenue Breakdown */}
      <div className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4">
        <h2 className="text-[15px] font-semibold text-adm-text-hi mb-4">Revenue Breakdown</h2>
        <ResponsiveContainer width="100%" height={300}>
          <AreaChart data={analyticsData.monthlyRevenue}>
            <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={chart.grid} />
            <XAxis dataKey="month" tickLine={false} axisLine={{ stroke: chart.grid }} />
            <YAxis tickLine={false} axisLine={false} width={44} />
            <Tooltip cursor={{ stroke: chart.grid, strokeDasharray: '3 3' }} />
            <Legend />
            <Area type="monotone" dataKey="new" stackId="1" stroke={chart.blue} fill={chart.blue} fillOpacity={0.75} name="New Vehicles" />
            <Area type="monotone" dataKey="used" stackId="1" stroke={chart.cmp} fill={chart.cmp} fillOpacity={0.75} name="Used Vehicles" />
            <Area type="monotone" dataKey="service" stackId="1" stroke={chart.chart2} fill={chart.chart2} fillOpacity={0.75} name="Service" />
            <Area type="monotone" dataKey="parts" stackId="1" stroke={chart.chart4} fill={chart.chart4} fillOpacity={0.75} name="Parts" />
          </AreaChart>
        </ResponsiveContainer>
      </div>

      {/* Top Performing Vehicles */}
      <div className="bg-adm-bg-1 rounded-adm-card shadow-adm-card p-4">
        <h2 className="text-[15px] font-semibold text-adm-text-hi mb-4">Top Performing Vehicles</h2>
        <div className="overflow-x-auto -mx-4 -mb-4">
          <table>
            <thead>
              <tr>
                <th>Vehicle</th>
                <th className="adm-num">Views</th>
                <th className="adm-num">Inquiries</th>
                <th className="adm-num">Test Drives</th>
                <th className="adm-num">Conversion Rate</th>
              </tr>
            </thead>
            <tbody>
              {analyticsData.topVehicles.map((vehicle, index) => (
                <tr key={index}>
                  <td className="whitespace-nowrap font-medium">{vehicle.name}</td>
                  <td className="adm-num">{vehicle.views}</td>
                  <td className="adm-num">{vehicle.inquiries}</td>
                  <td className="adm-num">{vehicle.testDrives}</td>
                  <td className="adm-num">{((vehicle.testDrives / vehicle.views) * 100).toFixed(1)}%</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default AdminAnalytics;
